/**
 * API Configuration
 * Production pages call https://api.signalbridges.com. Local paths stay relative.
 */
import { backendApiUrl } from './backendApiUrl';

// API prefix for all endpoints
export const API_PREFIX = '/api';

// SIMPLIFIED: No longer need ports or emulator configuration with Firebase Hosting rewrites

// SIMPLIFIED: All API calls now use relative URLs with Firebase Hosting rewrites

// No longer need base URL construction with Firebase Hosting rewrites

// API endpoints configuration - all now use relative paths
export const API_ENDPOINTS = {
  tokenExchange: '/api/auth/token-exchange',
  supportReports: '/api/supportReports',
  // Add other API endpoints here as needed
};

// Helper to construct API URLs with Firebase Hosting rewrites
export const getApiUrl = (endpoint, hostname) => {
  if (endpoint && /^https?:\/\//i.test(endpoint)) {
    return endpoint;
  }
  if (endpoint && (endpoint.startsWith('/api/') || endpoint === '/api')) {
    return backendApiUrl(endpoint, hostname);
  }

  const apiBaseUrl = import.meta.env.VITE_API_URL;

  if (!apiBaseUrl) {
    console.error(
      'VITE_API_URL is not defined. API calls will likely fail or use incorrect relative paths.'
    );
    // Fallback to a relative path, which might be intended if VITE_API_URL is deliberately unset for certain proxy setups.
    // However, for direct backend calls, this will lead to issues if an absolute URL was expected.
    return endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  }

  // Ensure the base URL doesn't have a trailing slash
  const cleanApiBaseUrl = apiBaseUrl.endsWith('/') ? apiBaseUrl.slice(0, -1) : apiBaseUrl;

  // Ensure the endpoint path starts with a slash
  const cleanEndpointPath = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;

  return `${cleanApiBaseUrl}${cleanEndpointPath}`;
};

// Default fetch options for API calls - simplified without CORS
export const DEFAULT_FETCH_OPTIONS = {
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
};

// Helper to make API requests with Firebase Hosting rewrites
export const fetchApi = async (endpoint, options = {}) => {
  const url = getApiUrl(endpoint);
  const fetchOptions = {
    ...DEFAULT_FETCH_OPTIONS,
    ...options,
    headers: {
      ...DEFAULT_FETCH_OPTIONS.headers,
      ...options.headers,
    },
  };

  console.log('🚀 API Request:', {
    url,
    method: options.method || 'GET',
    headers: fetchOptions.headers,
    mode: fetchOptions.mode,
    credentials: fetchOptions.credentials,
  });

  try {
    const response = await fetch(url, fetchOptions);
    if (!response.ok) {
      const error = await response.json().catch(() => ({
        message: response.statusText,
      }));
      throw new Error(error.message || 'API request failed');
    }
    return await response.json();
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
};

// Utility to add common headers to fetch requests
export const createApiHeaders = (additionalHeaders = {}) => ({
  'Content-Type': 'application/json',
  ...additionalHeaders,
});

// API request wrapper with logging
export const apiRequest = async (endpoint, options = {}) => {
  const url = getApiUrl(endpoint);
  const requestId = Math.random().toString(36).substring(7);
  const startTime = Date.now();

  console.debug(`🌐 [${requestId}] API Request:`, {
    url,
    method: options.method || 'GET',
    timestamp: new Date(startTime).toISOString(),
  });

  try {
    const response = await fetch(url, {
      ...options,
      headers: createApiHeaders(options.headers),
    });

    const endTime = Date.now();
    const duration = endTime - startTime;

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(`❌ [${requestId}] API Error:`, {
        status: response.status,
        statusText: response.statusText,
        duration: `${duration}ms`,
        error: errorData,
      });
      throw new Error(
        JSON.stringify({
          status: response.status,
          message: errorData.error || response.statusText,
        })
      );
    }

    const data = await response.json();
    console.log('🔍 Raw token response:', data);
    console.debug(`✅ [${requestId}] API Success:`, {
      status: response.status,
      duration: `${duration}ms`,
      timestamp: new Date(endTime).toISOString(),
    });

    return data;
  } catch (error) {
    console.error(`❌ [${requestId}] Request Failed:`, {
      error: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};
